<script lang="ts">
  import {
    Accordion,
    AccordionItem,
    Autocomplete,
    getToastStore,
    popup,
  } from '@skeletonlabs/skeleton'
  import { type AutocompleteOption, type PopupSettings, SlideToggle } from '@skeletonlabs/skeleton'
  import { onMount } from 'svelte'

  import InvalidHoverInfo from './InvalidHoverInfo.svelte'
  import InvalidIcon from './InvalidIcon.svelte'
  import {
    createStorageConfig,
    displayStorageTypes,
    type IStorageConfig,
    storageConfigStore,
    StorageType,
    userVisibleStorageTypeValues,
  } from '@routes/storage-configs/storage-configs-store'
  import StorageFormFields from '@routes/storage-configs/StorageFormFields.svelte'
  import { loggedInStore, type User } from '@stores/logged-in-store'
  import { ValidityState } from '@utils/validation.svelte'

  import Icon from '$lib/components/Icon.svelte'

  interface StorageConfiguratorProps {
    readOnly: boolean
    isEditMode: boolean
    invalids: Record<string, ValidityState>
    selectedStorageConfig: IStorageConfig | null
    isAccordionOpen: boolean
    inputValues: Record<string, unknown> & { selectedStorageConfigName: string }
    storageInputValues: Record<string, string | undefined>
    storageFormElement?: HTMLFormElement
    storageConfigName: string
    storageConfig?: IStorageConfig
    selectedStorageType: StorageType | null
    creating?: 'dataset' | 'model'
  }

  let {
    readOnly = $bindable(false),
    isEditMode = $bindable(false),
    invalids = $bindable({}),
    selectedStorageConfig = $bindable(null),
    isAccordionOpen = $bindable(false),
    inputValues = $bindable({ selectedStorageConfigName: '' }),
    storageInputValues = $bindable({}),
    storageFormElement = $bindable(),
    storageConfigName = $bindable(''),
    selectedStorageType = $bindable(null),
    storageConfig = $bindable(),
    creating,
  }: StorageConfiguratorProps = $props()

  let useLocalStorage = $state(false)

  onMount(() => {
    if (inputValues.selectedStorageConfigName && inputValues.selectedStorageConfigName !== '') {
      setStorageConfig(inputValues.selectedStorageConfigName)
    } else if (window.airGapped) {
      useLocalStorage = true
    }
  })

  const toastStore = getToastStore()
  const currentUser: User = $loggedInStore

  const LOCAL_STORAGE_CONFIG = {
    name: 'Local',
    type: StorageType.Local,
  }

  export const setStorageConfig = (selectedStorageConfigName: string) => {
    const foundStorageConfig = sortedStorageConfigs.find(
      (storageConfig) => storageConfig.name === selectedStorageConfigName,
    )

    if (foundStorageConfig && window.airGapped && foundStorageConfig.type === StorageType.Local) {
      useLocalStorage = true
      invalids.selectedStorageConfigName.setValid()
      return
    } else if (foundStorageConfig && foundStorageConfig.type === StorageType.Local) {
      invalids.selectedStorageConfigName.setInvalid(
        'Local storage is only available in air-gapped mode',
      )
      return
    }

    if (foundStorageConfig) {
      selectedStorageConfig = foundStorageConfig
      selectedStorageType = foundStorageConfig.type
      isAccordionOpen = false
      inputValues = {
        ...inputValues,
        selectedStorageConfigId: foundStorageConfig.id,
        selectedStorageConfigName: foundStorageConfig.name,
      }
      invalids.selectedStorageConfigName.setValid()
    } else {
      invalids.selectedStorageConfigName.setInvalid('No storage config with this name found')
    }
  }

  $effect(() => {
    const storageChanged = async (useLocalStorage: boolean) => {
      if (useLocalStorage) {
        selectedStorageType = StorageType.Local
        let localStorageConfig: IStorageConfig | null = null
        for (const config of $storageConfigStore.values()) {
          if (config.type === StorageType.Local) {
            localStorageConfig = config
            break
          }
        }
        if (!localStorageConfig) {
          const creatorId = currentUser.userId
          const creatorName = currentUser.fullName
          const storageConfigResponse = await createStorageConfig(
            LOCAL_STORAGE_CONFIG,
            toastStore,
            creatorId,
            creatorName,
          )
          localStorageConfig = {
            ...LOCAL_STORAGE_CONFIG,
            ...storageConfigResponse,
            creatorId,
            creatorName,
          }
        }
        selectedStorageType = StorageType.Local
        selectedStorageConfig = localStorageConfig
        inputValues.selectedStorageConfigName = `${localStorageConfig.name} (${displayStorageTypes[localStorageConfig.type]})`
        inputValues.selectedStorageConfigId = localStorageConfig.id
        invalids.selectedStorageConfigName.setValid()
        isAccordionOpen = false
      } else if (selectedStorageType === StorageType.Local) {
        selectedStorageType = null
        selectedStorageConfig = null
        inputValues.selectedStorageConfigName = ''
        inputValues.selectedStorageConfigId = ''
        invalids.selectedStorageConfigName.setValid()
      }
    }
    storageChanged(useLocalStorage)
  })

  let accordionDisabled = $derived(selectedStorageConfig !== null || isEditMode)

  let sortedStorageConfigs = $derived(
    Array.from($storageConfigStore.values())
      .filter((storageConfig) => userVisibleStorageTypeValues.includes(storageConfig.type))
      .sort((a, b) => a.name.localeCompare(b.name)),
  )

  function handleSearchInput(event: Event) {
    const input = (event.target as HTMLInputElement).value
    if (!input) {
      selectedStorageConfig = null // Reset the storage config
      isAccordionOpen = false // Ensure accordion can be reopened
    }
    const foundStorageConfig = sortedStorageConfigs.find(
      (storageConfig) =>
        `${storageConfig.name} (${displayStorageTypes[storageConfig.type]})` === input,
    )
    if (foundStorageConfig) {
      invalids.selectedStorageConfigName.setValid()
    } else {
      invalids.selectedStorageConfigName.setInvalid('No storage config with this name found')
      selectedStorageConfig = null
      selectedStorageType = null
    }
  }

  const storageConfigsListPopup: PopupSettings = {
    event: 'focus-click',
    target: 'popupAutocomplete',
    placement: 'top',
  }

  const chooseStorageConfigPopupHover: PopupSettings = {
    event: 'hover',
    placement: 'right',
    target: 'chooseStorageConfigPopupHover',
  }

  const disabledEditDatasetStoragePopup: PopupSettings = {
    event: 'hover',
    placement: 'top',
    target: 'disabledEditDatasetStorage',
  }

  const onStorageConfigSelect = (event: CustomEvent<AutocompleteOption<string>>) => {
    const selectedStorageConfigName = event.detail.value
    setStorageConfig(selectedStorageConfigName)
  }
</script>

{#if window.airGapped}
  <div>
    <SlideToggle bind:checked={useLocalStorage} name="useLocalStorage"
      >Use local storage</SlideToggle
    >
  </div>
{/if}
{#if !useLocalStorage}
  <p class="pb-2">
    Dataset storage:
    {#if !readOnly && !isEditMode}
      <button disabled class="[&>*]:pointer-events-none" use:popup={chooseStorageConfigPopupHover}>
        <Icon icon="iconoir:help-circle" class="text-tertiary-400" />
      </button>
      <span
        class="card variant-filled-tertiary z-50 p-2"
        data-popup="chooseStorageConfigPopupHover"
      >
        <span>Select an existing storage config or create a new one</span>
        <span class="variant-filled-tertiary arrow"></span>
      </span>
    {/if}
  </p>
  {#if readOnly}
    <div class="card variant-glass-surface p-3 {readOnly ? 'mt-2' : ''}">
      <StorageFormFields
        bind:storageFormElement
        bind:inputValues={storageInputValues}
        bind:selectedStorageType
        bind:storageConfigName
        {storageConfig}
        {readOnly}
        {creating}
      ></StorageFormFields>
    </div>
  {:else}
    <div class="relative" use:popup={disabledEditDatasetStoragePopup}>
      <div
        class={`card variant-filled-warning p-2 ${isEditMode ? 'visible' : 'invisible'}`}
        data-popup="disabledEditDatasetStorage"
      >
        <p>You cannot change the dataset storage in edit mode</p>
        <div class="variant-filled-warning arrow"></div>
      </div>
      <div class="input-group input-group-divider grid-cols-[1fr_auto]">
        <input
          class="my-0 box-border flex px-2 py-1 md:min-w-96"
          type="search"
          name="autocomplete-search"
          placeholder="Search..."
          disabled={isAccordionOpen || isEditMode}
          autocomplete="off"
          required={!isAccordionOpen}
          bind:value={inputValues.selectedStorageConfigName}
          use:popup={storageConfigsListPopup}
          oninput={handleSearchInput}
        />
        <InvalidIcon
          {invalids}
          field="selectedStorageConfigName"
          value={inputValues.selectedStorageConfigName}
        />
      </div>
      <InvalidHoverInfo {invalids} field="selectedStorageConfigName" />
      <div
        class="card variant-filled-surface absolute z-50 max-h-48 w-full max-w-sm overflow-y-auto p-4"
        tabindex="-1"
        data-popup="popupAutocomplete"
      >
        <Autocomplete
          bind:input={inputValues.selectedStorageConfigName}
          options={sortedStorageConfigs.map((storageConfig) => ({
            value: storageConfig.name,
            label: `${storageConfig.name} (${displayStorageTypes[storageConfig.type]})`,
          }))}
          on:selection={onStorageConfigSelect}
          on:input={handleSearchInput}
          regionButton="w-full text-left"
        />
      </div>
      {#if !isEditMode}
        <div class="mt-4" class:disable-accordion={accordionDisabled}>
          <Accordion class="card variant-glass-surface">
            <AccordionItem
              on:click={() => (isAccordionOpen = !isAccordionOpen)}
              open={isAccordionOpen}
              disabled={accordionDisabled}
            >
              {#snippet summary()}
                Create new storage config
              {/snippet}
              {#snippet iconOpen()}
                <Icon icon="iconoir:plus" />
              {/snippet}
              {#snippet iconClosed()}
                <Icon icon="iconoir:minus" />
              {/snippet}
              {#snippet content()}
                <StorageFormFields
                  bind:storageFormElement
                  bind:inputValues={storageInputValues}
                  bind:selectedStorageType
                  bind:storageConfigName
                  {storageConfig}
                  {creating}
                ></StorageFormFields>
              {/snippet}
            </AccordionItem>
          </Accordion>
        </div>
      {/if}
    </div>
  {/if}
{/if}

<style lang="postcss">
  input[type='search']::-webkit-search-cancel-button {
    filter: invert(1); /* Invert to white if the background is dark */
  }
</style>
